import {createStyles} from '@mantine/core';
import classNames from 'classnames';
import React from 'react';

import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE_LIGHT, GRAY_DARKER, GRAY_LIGHT, NEAR_BLACK, WHITE} from 'ui-library/Colors';
import useHoverState from 'ui-library/hooks/useHoverState';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

interface ApplicationCardProps {
  onClick: () => void;
  titleText: string;
  bodyText: string;
  imagePath?: string;
  icon?: BenchlingIconName;
  iconVariant?: 'default' | 'inverse';
  buttonText: string;
  hoverOn?: () => void;
  hoverOff?: () => void;
  compact?: boolean;
}

interface SignupScreenCardStyleParams {
  compact: boolean;
  iconVariant: 'default' | 'inverse';
  redesigned: boolean;
}

const useStyles = createStyles((theme, {compact, iconVariant, redesigned}: SignupScreenCardStyleParams) => ({
  container: {
    alignItems: redesigned ? 'flex-start' : compact ? 'center' : undefined,
    backgroundColor: redesigned ? theme.white : undefined,
    border: `1px solid ${redesigned ? theme.colors.gray[2] : GRAY_LIGHT}`,
    borderRadius: redesigned ? theme.radius.smplus : '3px',
    display: 'flex',
    flexShrink: compact ? 0 : undefined,
    gap: redesigned ? theme.spacing.xl : undefined,
    height: redesigned ? undefined : compact ? '148px' : '184px',
    justifyContent: 'flex-start',
    padding: redesigned ? theme.spacing.xxl : undefined,
    width: redesigned ? '100%' : '340px',
    ':hover': {
      boxShadow: '0px 2px 6px rgba(162, 185, 190, 0.7)',
    },
  },
  iconContainer: {
    alignItems: 'center',
    background:
      iconVariant === 'inverse'
        ? `linear-gradient(135deg, ${theme.colors.blue[8]} 0%, ${theme.colors.blue[6]} 100%)`
        : theme.colors.blue[0],
    borderRadius: theme.radius.md,
    display: redesigned ? 'flex' : 'none',
    flexShrink: 0,
    height: '72px',
    justifyContent: 'center',
    width: '72px',
  },
  image: {
    height: compact ? '80px' : '100px',
    width: compact ? '80px' : '100px',
    marginTop: compact ? 0 : '42px',
    marginLeft: compact ? theme.spacing.lg : '21px',
  },
  icon: {
    color: iconVariant === 'inverse' ? theme.white : theme.colors.blue[7],
    fontSize: '36px',
    height: '36px',
    width: '36px',
  },
  textBox: {
    display: redesigned ? 'flex' : undefined,
    flexDirection: redesigned ? 'column' : undefined,
    flexGrow: redesigned ? 1 : undefined,
    gap: redesigned ? '6px' : undefined,
    minWidth: redesigned ? 0 : undefined,
    marginTop: redesigned || compact ? 0 : '33px',
    marginLeft: redesigned ? 0 : compact ? theme.spacing.xl : theme.spacing.xxl,
    marginRight: redesigned ? 0 : theme.spacing.lg,
  },
  titleText: {
    color: redesigned ? theme.colors.gray[9] : NEAR_BLACK,
    fontSize: redesigned ? '20px' : '18px',
    fontWeight: redesigned ? theme.other.fontWeights.bold : undefined,
    lineHeight: redesigned ? 1.2 : '24px',
  },
  bodyText: {
    color: redesigned ? theme.colors.gray[6] : GRAY_DARKER,
    fontSize: redesigned ? '14px' : '12px',
    lineHeight: redesigned ? 1.5 : '14px',
    paddingTop: redesigned ? undefined : theme.spacing.sm,
    textWrap: redesigned ? 'pretty' : undefined,
  },
  button: {
    alignSelf: redesigned ? 'flex-start' : undefined,
    marginTop: redesigned ? '10px' : theme.spacing.sm,
  },
  hoverButton: {
    backgroundColor: BLUE_LIGHT,
    color: WHITE,
  },
}));

export default function SignupScreenCard(props: ApplicationCardProps): JSX.Element {
  const [isHovered, hoverProps] = useHoverState();

  const {onClick, titleText, bodyText, imagePath, icon, iconVariant, buttonText, hoverOn, hoverOff, compact} =
    props;
  const redesigned = icon != null;
  const {classes, cx} = useStyles({
    compact: compact ?? false,
    iconVariant: iconVariant ?? 'default',
    redesigned,
  });

  React.useEffect(() => {
    if (hoverOn && isHovered) {
      hoverOn();
    }
    if (hoverOff && !isHovered) {
      hoverOff();
    }
  }, [isHovered, hoverOn, hoverOff]);

  return (
    <div className={classNames(classes.container, 'u-pointer')} onClick={onClick} {...hoverProps}>
      {icon != null ? (
        <div className={classes.iconContainer} aria-hidden={true}>
          <BenchlingIcon icon={icon} className={classes.icon} />
        </div>
      ) : (
        imagePath != null && (
          <span>
            {/* Decorative image; the adjacent title conveys the card's meaning to screen readers. */}
            <img alt="" src={imagePath} className={classes.image} />
          </span>
        )
      )}
      <span className={classNames({'u-flex-grow': !redesigned}, classes.textBox)}>
        <div className={classes.titleText}>{titleText}</div>
        <div className={classes.bodyText}>{bodyText}</div>
        <Button
          className={cx(classes.button, {[classes.hoverButton]: !redesigned && isHovered})}
          text={buttonText}
          buttonStyle={redesigned ? ButtonStyle.PRIMARY : ButtonStyle.TERTIARY_BLUE}
          size={redesigned ? ButtonSize.SMALL : ButtonSize.MEDIUM}
        />
      </span>
    </div>
  );
}
